// Pricing curve types, used for api calls to retrieve the
// plans and packages that the plan builder can sell

import {Agreement} from 'common/types/app-api/billing_bump/types';

import {SliderNotchSize} from '../plan-builder-2023/step-components/plan-builder-step-slider-25k/types';

export enum BillingFrequency {
  MONTHLY = 1,
  ANNUAL = 12,
  CUSTOM = 0, // To handle edge cases (e.g. quarterly self-serve events plans)
}

export interface BillingPeriod {
  startDate: Date;
  endDate: Date;
}

export interface PricingCurvePlanPoint {
  // One individual "point" on the pricing curve; used to populate the slider.
  //
  readonly cap: number;
  readonly billingFrequency: BillingFrequency;
  readonly price: string;
  readonly isEnterprise?: boolean;
}

export interface ExperimentalCurvePoint {
  readonly cap: number;
  readonly price: number;
  readonly overageRate: string;
}

export enum PackageId {
  // Deprecated 0
  DATA_WAREHOUSE_EXPORT = 1,
  GROUP_ANALYTICS = 2,
}

export const PACKAGE_NAMES_BY_ID = {
  [PackageId.DATA_WAREHOUSE_EXPORT]: `datawarehouse_export_v1`,
  [PackageId.GROUP_ANALYTICS]: `groups_v1_x2`,
};

export const PACKAGE_ID_BY_NAME = {
  datawarehouse_export_v1: PackageId.DATA_WAREHOUSE_EXPORT,
  groups_v1_x2: PackageId.GROUP_ANALYTICS,
};

export interface PricingCurvePackage {
  // A package available for sale; its price is basePlanPct of
  // the price of the selected PricingCurvePlanPoint. Name and
  // description are used in UI elements for display.
  readonly id: PackageId;
  readonly name: string;
  readonly description: string;
  readonly basePlanPct: number;
}

export enum PricingCurveId {
  // An identifier for a set of plans that should be treated similarly.
  V1_GROWTH = 0,
  V2_GROWTH = 1,
  V2_1_GROWTH = 2,
  V2_1_1_GROWTH = 3,
  PRICING_2023 = 4,
  LENGTH = 5, // Member used for tests; increment this when you add a new member
}

export type SliderNotch = {
  label: string;
  alwaysShowLabel: boolean;
  value: number;
  notchSize: SliderNotchSize;
  width?: number;
};

type MetadataPlans = {
  monthly: PricingCurveMetadata;
  annual: PricingCurveMetadata;
  enterprise: PricingCurveMetadata;
};

type PricingCurveMetadata = {
  unit_cap: number;
  slider_data: SliderNotch[];
};

export enum PricingCurveVariant {
  GROWTH = `growth`,
  ENTERPRISE = `enterprise`,
}

export interface PricingCurve {
  // A set of plans and packages with prices that can be sold through the plan builder.
  readonly id: PricingCurveId;
  readonly points: PricingCurvePlanPoint[];
  readonly packages: PricingCurvePackage[];
  readonly metadata?: MetadataPlans;
}

export interface ExperimentalPricingCurve {
  readonly planId: string;
  readonly agreementGroupId: number;
  readonly curveName: string;
  readonly billingFrequency: BillingFrequency;
  readonly sliderInfo: SliderNotch[];
  readonly points: {
    [k: string]: ExperimentalCurvePoint;
  };
  readonly packages: PricingCurvePackage[];
  readonly isEnterprise: boolean;
  readonly excludedQuantityFromOverage?: number;
  readonly defaultOverageMultiplier?: number;
}

export const MTU_GROWTH_OVERAGE_MULTIPLIER = 1.2;

// Plan and package state types
// These are used for keeping track of the user's current state and selected state

export enum CustomerType {
  // Used for identifying the "world" the customer's billing state is in.
  NONE = 0, // Customer is not authenticated or is otherwise on no plan
  FREE_LEGACY = 1, // Customer is on free events + people plans
  FREE_MTU = 2, // Customer is on the old 1k free MTU plan
  FREE_MTU_100K = 3, // Customer is on the new 100k free MTU plan
  SELFSERVE_LEGACY = 4, // Customer is on a self-serve events and/or people plan
  SELFSERVE_MTU = 5, // Customer is on a self-serve MTU plan
  CONTRACT_LEGACY = 6, // Customer is on a contract but not an MTU enterprise plan
  CONTRACT_MTU = 7, // Customer is on a contract MTU enterprise plan
  GENERIC_LEGACY = 8, // Customer is on some random legacy plan that we don't need get as granular with
  FREE_PRICING_2023 = 9, // Customer is on free pricing experiment 2023 (events) plan
  PREMIUM_PRICING_2023 = 10, // Customer is on premium pricing experiment 2023 (events) plan
}

export interface PlanState {
  customerType: CustomerType;
  curveId?: PricingCurveId;
  cap: number;
  billingFrequency: BillingFrequency;
  packageIds: PackageId[];
  curveVariant?: PricingCurveVariant;
  isNewStartupProgramCustomer?: boolean;
  bumpCurveId?: number;
}

export interface PaymentInfo {
  countryCode: string;
  postalCode: string;
  paymentMethod: string;
}

export interface SelectedUpdate {
  planChanges: Partial<PlanState>;
  paymentInfoChanges: Partial<PaymentInfo>;
}

// User and account info types
// These are used to present user and account information to the plan builder.
// This is for things that the plan builder doesn't or can't change.

export interface CreditCardInfo {
  // Human-readable representation of the customer's current payment method, if they have one
  type: string; // e.g visa or master card
  last4: string;
  expiryMonth: number;
  expiryYear: number;
  countryCode: string;
  postalCode: string;
}

export interface CreditsInfo {
  // Amount of plan and package credits the customer has
  plan: number;
  package: number;
}

export interface LegacyPlanDescriptionLine {
  readonly description: string;
  readonly price: string;
  readonly dateString: string;
  readonly packages: string[];
}

export interface OrgPlanInfo {
  readonly orgId: number;
  readonly accountId: number;
  readonly canDowngradeOrTransitionOnline: boolean;
  readonly canPurchaseOnline: boolean;
  readonly hasBillingPermissions: boolean;
  readonly currentPlanState: PlanState;

  readonly orgName: string;
  readonly mtuUsage: number;
  readonly usageWasLastMonth: boolean;
  readonly arr: number;

  readonly creditsInfo?: CreditsInfo;
  readonly billingCredits?: BillingCredit[];
  readonly eventsBillingPeriod?: BillingPeriod;
  readonly peopleBillingPeriod?: BillingPeriod;
  readonly legacyPlanDescription?: LegacyPlanDescriptionLine[];

  readonly isExperimentPlan?: boolean;

  readonly isOnBump: boolean;
  readonly isBumpPbEnabled: boolean;
  readonly agreements?: Agreement[];
  readonly isPaid?: boolean;
}

export enum BillingCreditState {
  VALID = `valid`,
  REDEEMED = `redeemed`,
  EXPIRED = `expired`,
  NOT_EFFECTIVE = `not_effective`,
}

export interface BillingCredit {
  appliesTo: string;
  endDate: string;
  initialValue: number;
  name: string;
  remainingBalance: number;
  state: BillingCreditState;
}

export interface UserInfo {
  readonly id: number;
  readonly name: string;
  readonly email: string;
}

export interface CustomerInfo {
  readonly isAuthenticated: boolean;
  readonly curveId: PricingCurveId;
  readonly userInfo?: UserInfo;
  readonly orgInfo?: OrgPlanInfo;
}

export interface AmountDueLine {
  amount: string;
  description: string;
  state: string;
}

export interface AmountDueInfo {
  amount: number;
  creditsApplied?: number;
  currentBalance?: number;
  lines?: AmountDueLine[];
  startDate?: string;
  renewalDate?: string;
  is_annual_downgrade?: boolean;
  is_bump_purchase?: boolean;
}

export type CurrentPlanBreakdownRow = {
  description?: string;
  price?: string;
  dateString?: string;
};

export type BreakdownLineItem = {
  amount?: string;
  description: string;
  numberIsGreen?: boolean;
  textIsGrey?: boolean;
  infoText?: string;
  indent?: boolean;
};
